// ═══════════════════════════════════════════════════════════════════════════
// La liga del ticket
//
// El operador: "El ticket debería poder usarse por cualquiera; por ejemplo si
// no sé el correo del vendedor, pero tengo su número para mandarle un texto.
// Debería tener el URL para que yo pudiera generar, pegar y mandar."
//
// ── POR QUÉ VA EN EL FRAGMENTO (#) Y NO EN LA QUERY (?) ──────────────────
//
// Lo que va después del `#` NUNCA se manda al servidor. No aparece en los
// registros de Cloudflare, no viaja en la cabecera Referer si la página enlaza
// a otro sitio, y no queda en los registros de ningún intermediario.
//
// Con `?t=CODIGO` el código quedaría escrito en los accesos del servidor. Para
// un enlace que da acceso a los datos de una vinícola, esa diferencia importa.
//
// Sigue siendo un secreto que viaja por WhatsApp o SMS, y eso no lo arregla
// ninguna decisión técnica: quien reciba el mensaje reenviado puede usarlo.
// Por eso el ticket sirve UNA vez y vence — el daño posible tiene límite.
// ═══════════════════════════════════════════════════════════════════════════

const CLAVE = "t";
const GUARDADO = "vt-ticket-pendiente";

const VALIDO = /^[A-Z0-9]{5}-[A-Z0-9]{5}$/;

/**
 * El código pendiente: el de la liga, o el que se guardó de una visita previa.
 *
 * SE GUARDA porque el camino real da una vuelta: el vendedor abre la liga,
 * crea su cuenta, y el correo de confirmación lo trae de regreso SIN el
 * fragmento. Sin recordarlo, al volver tendría que teclear el código a mano
 * justo cuando ya cree que terminó.
 */
export function ticketDeLaLiga() {
  try {
    const h = (window.location.hash || "").replace(/^#/, "");
    if (h) {
      const c = (new URLSearchParams(h).get(CLAVE) || "").trim().toUpperCase();
      if (VALIDO.test(c)) {
        try { localStorage.setItem(GUARDADO, c); } catch { /* sin almacenamiento */ }
        return c;
      }
    }
    const g = (localStorage.getItem(GUARDADO) || "").trim().toUpperCase();
    return VALIDO.test(g) ? g : null;
  } catch { return null; }
}

/** Se llama al canjear con éxito: ya no hay nada pendiente que recordar. */
export function olvidarTicket() {
  try { localStorage.removeItem(GUARDADO); } catch { /* nada que hacer */ }
}

/**
 * Borra el código de la barra de direcciones.
 *
 * Se llama en cuanto se lee. Dejarlo ahí lo deja en el historial del
 * navegador y a la vista de quien pase por atrás — y ya no sirve de nada,
 * porque la app se lo quedó en memoria.
 */
export function limpiarLiga() {
  try {
    window.history.replaceState(null, "",
      window.location.pathname + window.location.search);
  } catch { /* navegador sin history API */ }
}

/** La liga que se le manda al vendedor. */
export function ligaDeTicket(codigo) {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}#${CLAVE}=${codigo}`;
}

/**
 * El mensaje listo para pegar en WhatsApp o en un correo.
 *
 * Se arma aquí y no en el componente para que el texto sea uno solo: si
 * mañana cambia lo que hay que explicarle a un vendedor nuevo, cambia en un
 * lugar y no en tres pantallas.
 *
 * ── EL CÓDIGO YA NO VA EN EL MENSAJE ───────────────────────────────────────
 *
 * Lo reportó el operador después de dar de alta a un vendedor de verdad: "se
 * confunde con el código, la contraseña y el correo".
 *
 * Y con razón: el mensaje traía la liga Y el código suelto, y luego la pantalla
 * le pedía crear una cuenta. Tres cosas distintas para hacer una. El código va
 * DENTRO de la liga —siempre estuvo— así que enseñarlo aparte no agregaba una
 * opción, agregaba una duda: "¿esto lo tengo que escribir en algún lado?".
 *
 * Ahora el mensaje tiene UNA cosa que hacer. El código sigue existiendo y la
 * pantalla de quien invita lo puede dictar por teléfono si la liga no llega,
 * pero eso es el camino de excepción y ya no ensucia el normal.
 *
 * ── Y NO SE MENCIONA NINGUNA CUENTA ────────────────────────────────────────
 *
 * El texto decía "crea tu cuenta", que describía el mundo anterior a 0.35.0.
 * Desde entonces abrir la liga es un botón y no hay correo ni contraseña de por
 * medio. Un mensaje que anuncia trabajo que ya no existe crea justo la
 * confusión que se reportó.
 */
export function mensajeDeInvitacion(codigo, { vinicola, expira } = {}) {
  const liga = ligaDeTicket(codigo);
  const vence = expira ? fechaLarga(expira) : null;
  return [
    `Te doy acceso a ${vinicola || "la aplicación"}.`,
    ``,
    `Ábrela en tu teléfono y listo:`,
    liga,
    ``,
    `No necesitas correo ni contraseña.`,
    vence ? `La liga sirve una sola vez y vence el ${vence}.`
          : `La liga sirve una sola vez.`,
  ].join("\n");
}

/**
 * La fecha, en palabras y SIN perder un día por la zona horaria.
 *
 * `new Date("2026-08-19")` —una fecha sin hora— se interpreta como medianoche
 * UTC, que en Ensenada es el 18 a las 5 de la tarde. El mensaje le habría dicho
 * al vendedor que su liga vence un día antes de lo que vence.
 *
 * Hoy la base manda un timestamp con zona y esto no pasa. Se blinda igual
 * porque el error es invisible —la fecha se ve razonable, solo está mal— y
 * porque esta misma trampa ya se documentó al agrupar remisiones por mes.
 */
function fechaLarga(valor) {
  const soloFecha = typeof valor === "string" && /^\d{4}-\d{2}-\d{2}$/.test(valor);
  const d = new Date(soloFecha ? `${valor}T12:00:00` : valor);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "long" });
}
